import { formatTrackingNumberWithEllipsis } from '@frontend/common';
import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedTransaction, ExtendedVendingTransaction } from '@frontend/transaction/types';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { DetailCard } from '../../components/detail-card.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { formatEntityNameWithExtra } from './format-entity-name-with-extra';
import { formatDimension, useSlotDetail } from './slot-detail.controller';
import { StockCard } from './stock-card';
import { UpdateTransactionStatusModal } from './update-transaction-status-modal.component';

const SlotDetail = () => {
    const viewProps = useSlotDetail();
    const { slot } = viewProps;

    return (
        <div className='bg-background-default flex size-full flex-col overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={viewProps.handleBack}
            />

            {viewProps.isLoading && (
                <div className='flex flex-1 items-center justify-center p-6'>
                    <Spinner />
                </div>
            )}
            {!viewProps.isLoading && viewProps.hasError && (
                <div className='text-status-danger flex flex-1 items-center justify-center p-6'>
                    {viewProps.error ?? CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT)}
                </div>
            )}
            {!viewProps.isLoading && !viewProps.hasError && slot && (
                <div className='text-foreground-default flex size-full flex-col items-start gap-6 overflow-y-auto p-6 font-medium'>
                    <div className='flex w-full flex-col items-start gap-3'>
                        <FormattedMessage
                            id='kiosk.manage-spot.view-content-slot-detail.slot-details'
                            defaultMessage='SLOT details'
                        />
                        <DetailCard title={null}>
                            <DetailCard.Row
                                label={
                                    <FormattedMessage
                                        id='kiosk.manage-spot.view-content-slot-detail.slot-name'
                                        defaultMessage='SLOT name'
                                    />
                                }
                                value={slot.display ?? slot.number ?? '/'}
                            />
                            {/* <DetailCard.Row
                                label={CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}
                                value={<SlotStatusBadge status={slot.status} />}
                            /> */}
                            <DetailCard.Row
                                label={
                                    <FormattedMessage
                                        id='kiosk.manage-spot.view-content-slot-detail.size'
                                        defaultMessage='Size'
                                    />
                                }
                                value={formatDimension(slot.inner_dimension ?? slot.dimension)}
                            />
                        </DetailCard>
                    </div>

                    <div className='flex w-full flex-col items-start gap-3'>
                        {CommonMessage.OBJECTS.TRANSACTION.PLURAL}
                        {viewProps.transactions.length > 0 ? (
                            <div className='flex w-full flex-col gap-4'>
                                {viewProps.transactions.map((tx) => (
                                    <Fragment key={tx.id}>
                                        <DetailCard title={null}>
                                            <DetailCard.Row
                                                label={CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}
                                                value={<TransactionStateBadge state={tx.state} />}
                                            />
                                            <DetailCard.Row
                                                label={CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                                                value={formatTrackingNumberWithEllipsis(tx.tracking_number ?? '') || '/'}
                                            />
                                            {'product' in tx && (tx as ExtendedVendingTransaction).product ? (
                                                <>
                                                    <DetailCard.Row
                                                        label={CommonMessage.OBJECTS.PRODUCT.DEFAULT}
                                                        value={(tx as ExtendedVendingTransaction).product?.data?.name ?? '/'}
                                                    />
                                                    <DetailCard.Row
                                                        label={CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY}
                                                        value={(tx as ExtendedVendingTransaction).quantity ?? '/'}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <DetailCard.Row
                                                        label={CommonMessage.OBJECTS.TRANSACTION.SENDER}
                                                        value={'senders' in tx ? formatEntityNameWithExtra((tx as ExtendedTransaction).senders) : '/'}
                                                    />
                                                    <DetailCard.Row
                                                        label={CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                                        value={'receivers' in tx ? formatEntityNameWithExtra((tx as ExtendedTransaction).receivers) : '/'}
                                                    />
                                                </>
                                            )}
                                        </DetailCard>
                                        <DetailCard
                                            title={
                                                <FormattedMessage
                                                    id='kiosk.manage-spot.pudo-transactions.actions'
                                                    defaultMessage='Actions'
                                                    description='Title for the actions card on the manage-spot PuDo transaction detail screen'
                                                />
                                            }>
                                            <div className='flex flex-wrap gap-4'>
                                                <button
                                                    type='button'
                                                    className='text-brand-primary'
                                                    onClick={() => viewProps.openUpdateStatusModal(tx)}>
                                                    <FormattedMessage
                                                        id='kiosk.manage-spot.slot-detail.update-status'
                                                        defaultMessage='Update status'
                                                    />
                                                </button>
                                                {viewProps.canMoveTransaction(tx) && (
                                                    <button
                                                        type='button'
                                                        className='text-brand-primary'
                                                        onClick={() => viewProps.navigateToMove(tx)}>
                                                        {CommonMessage.BUTTONS.MOVE}
                                                    </button>
                                                )}
                                            </div>
                                        </DetailCard>
                                    </Fragment>
                                ))}
                            </div>
                        ) : (
                            <div className='bg-background-surface border-border-default w-full rounded-xl border p-4'>
                                <span className='text-brand-muted'>
                                    <FormattedMessage
                                        id='kiosk.manage-spot.view-content-slot-detail.no-transactions'
                                        defaultMessage='No transactions'
                                    />
                                </span>
                            </div>
                        )}
                    </div>

                    <div className='flex w-full flex-col items-start gap-3'>
                        <div className='flex w-full justify-between'>
                            <div>{CommonMessage.OBJECTS.STOCK.DEFAULT}</div>
                            {viewProps.canAddStock && (
                                <div
                                    onClick={() => viewProps.navigate('./add-product')}
                                    className='text-brand-primary justify-start text-base font-medium'>
                                    <FormattedMessage
                                        id='slot-detail.stock.add-product'
                                        defaultMessage='Add product'
                                    />
                                </div>
                            )}
                        </div>
                        {viewProps.stocks.length > 0 ? (
                            <div className='flex w-full flex-col gap-4'>
                                {viewProps.stocks.map((stock) => (
                                    <StockCard
                                        key={stock.id}
                                        stock={stock}
                                        onRemove={viewProps.fetchStocks}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className='bg-background-surface border-border-default w-full rounded-xl border p-4'>
                                <span className='text-brand-muted'>
                                    <FormattedMessage
                                        id='kiosk.manage-spot.view-content-slot-detail.no-stock'
                                        defaultMessage='No stock'
                                    />
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!viewProps.isLoading && (
                <KioskNavigationFooter
                    leftButton={
                        <DefaultSecondaryButton
                            text={CommonMessage.BUTTONS.GO_BACK}
                            onClick={viewProps.handleBack}
                        />
                    }
                    rightButton={
                        viewProps.canOpenSlot ? (
                            <DefaultPrimaryButton
                                text={
                                    <FormattedMessage
                                        id='kiosk.manage-spot.view-content-slot-detail.open-slot'
                                        defaultMessage='Open SLOT {name}'
                                        values={{ name: slot?.display ?? '' }}
                                    />
                                }
                                onClick={viewProps.handleOpenSlot}
                                disabled={viewProps.isOpeningSlot}
                                isLoading={viewProps.isOpeningSlot}
                            />
                        ) : null
                    }
                />
            )}

            <UpdateTransactionStatusModal
                transaction={viewProps.transactionForStatusModal}
                show={viewProps.updateStatusModalOpen}
                onClose={viewProps.closeUpdateStatusModal}
                onSuccess={viewProps.handleUpdateStatusSuccess}
            />
        </div>
    );
};

export default SlotDetail;
